import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';

const PrivacidadPage = () => {
    const { theme, toggleTheme } = useTheme();

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Política de Privacidad – Laptops.pe</title>
                <meta name="description" content="Política de Privacidad de Laptops.pe, operada por Digital Wins. Última actualización: Octubre 2025." />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || ''}/privacidad`} />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Header theme={theme} onToggleTheme={toggleTheme} />

            <main className="landing-main">
                <div className="py-6">
                    <div className="text-center mb-5">
                        <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-2">
                            <span role="img" aria-label="candado">🔒</span> Política de Privacidad – Laptops.pe
                        </h1>
                        <p className="text-700">Última actualización: <strong>Octubre 2025</strong></p>
                    </div>

                    <Card className="mb-4">
                        <div className="p-4">
                            <p className="text-700 line-height-3">
                                En <strong>Laptops.pe</strong>, operada por <strong>Digital Wins</strong>, respetamos la privacidad y seguridad de los datos personales de nuestros clientes y usuarios.
                                Esta Política explica cómo recopilamos, utilizamos, protegemos y tratamos la información personal conforme a la <strong>Ley de Protección de Datos Personales N° 29733</strong> y su reglamento.
                            </p>
                            <p className="text-700 line-height-3">
                                Al usar nuestro sitio web o cualquiera de nuestros servicios digitales, usted acepta los términos descritos en esta Política.
                            </p>
                        </div>
                    </Card>

                    <Card className="mb-4">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-id-card text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">1. Identificación del responsable del tratamiento</h2>
                            </div>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <Panel header="Razón comercial">
                                        <p className="m-0 text-700">Digital Wins</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="RUC">
                                        <p className="m-0 text-700">20612859630</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Nombre comercial">
                                        <p className="m-0 text-700">Laptops.pe</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Correo de contacto">
                                        <p className="m-0 text-700"><a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a></p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Domicilio legal">
                                        <p className="m-0 text-700">Lima, Perú</p>
                                    </Panel>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Panel header="2. Datos que recopilamos" className="mb-4">
                        <p className="text-700">Podemos recopilar los siguientes tipos de información:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li><strong>Datos personales:</strong> nombres, apellidos, número de DNI/RUC, dirección, correo electrónico, teléfono, y datos de facturación.</li>
                            <li><strong>Datos de navegación:</strong> dirección IP, tipo de dispositivo, navegador, ubicación aproximada y páginas visitadas.</li>
                            <li><strong>Datos de pago:</strong> únicamente los necesarios para validar transacciones, procesados de forma segura por entidades financieras o pasarelas externas (no almacenamos información de tarjetas).</li>
                            <li><strong>Datos de contacto con soporte o redes sociales:</strong> mensajes, consultas o comentarios enviados a nuestros canales oficiales.</li>
                        </ul>
                    </Panel>

                    <Panel header="3. Finalidad del tratamiento de datos" className="mb-4">
                        <p className="text-700">Los datos personales serán utilizados con las siguientes finalidades:</p>
                        <ol className="list-decimal ml-4 text-700">
                            <li>Procesar pedidos y realizar la entrega de productos.</li>
                            <li>Emitir comprobantes de pago electrónicos conforme a SUNAT.</li>
                            <li>Brindar soporte técnico y atención al cliente.</li>
                            <li>Enviar notificaciones sobre el estado del pedido o promociones relevantes.</li>
                            <li>Cumplir con obligaciones legales, contables o tributarias.</li>
                            <li>Prevenir fraudes y verificar la autenticidad de las transacciones.</li>
                            <li>Mejorar la experiencia de navegación y optimizar nuestros servicios.</li>
                        </ol>
                        <p className="text-700 mt-3">En ningún caso se solicitará información innecesaria, excesiva o no relacionada con las finalidades descritas.</p>
                    </Panel>

                    <Panel header="4. Base legal del tratamiento" className="mb-4">
                        <p className="text-700">El tratamiento de los datos personales se realiza conforme a la <strong>Ley N° 29733</strong>, bajo las siguientes bases:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Consentimiento del titular.</li>
                            <li>Ejecución de una relación contractual (compra o prestación de servicios).</li>
                            <li>Cumplimiento de obligaciones legales aplicables a Digital Wins.</li>
                            <li>Interés legítimo para fines de seguridad, atención y mejora del servicio.</li>
                        </ul>
                    </Panel>

                    <Panel header="5. Transferencia y almacenamiento" className="mb-4">
                        <p className="text-700">Los datos recopilados se almacenan en <strong>servidores seguros ubicados en el Perú o en proveedores de infraestructura tecnológica con estándares internacionales de seguridad (como AWS, Google Cloud o similares)</strong>.</p>
                        <p className="text-700"><strong>Digital Wins</strong> garantiza que toda transferencia de información a terceros cumple las disposiciones de confidencialidad y seguridad exigidas por ley. No se venden, alquilan ni transfieren datos personales a terceros no autorizados bajo ningún concepto.</p>
                    </Panel>

                    <Panel header="6. Uso de cookies y tecnologías similares" className="mb-4">
                        <p className="text-700">Laptops.pe utiliza <strong>cookies</strong> y herramientas de análisis (como Google Analytics, Meta Pixel y TikTok Pixel) para medir el tráfico, mejorar la experiencia del usuario y optimizar campañas publicitarias.</p>
                        <p className="text-700">El usuario puede configurar su navegador para rechazar cookies, aunque algunas funciones del sitio podrían no operar correctamente.</p>
                    </Panel>

                    <Panel header="7. Tratamiento por terceros" className="mb-4">
                        <p className="text-700">Podemos compartir datos personales únicamente con terceros necesarios para el cumplimiento de nuestras operaciones, tales como:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Pasarelas de pago (VisaNet, Mercado Pago, Niubiz, entre otros).</li>
                            <li>Empresas de transporte y logística.</li>
                            <li>Plataformas de marketing y análisis estadístico.</li>
                            <li>Proveedores de hosting, mantenimiento web y seguridad.</li>
                        </ul>
                        <p className="text-700 mt-3">Todos estos terceros están <strong>obligados contractualmente</strong> a garantizar la confidencialidad y protección de la información.</p>
                    </Panel>

                    <Panel header="8. Seguridad de la información" className="mb-4">
                        <p className="text-700"><strong>Digital Wins</strong> adopta medidas técnicas, administrativas y organizativas adecuadas para proteger los datos personales contra pérdida, acceso no autorizado, alteración o destrucción.</p>
                        <p className="text-700">Sin embargo, el usuario reconoce que <strong>ningún sistema digital es 100% infalible</strong>, y acepta que el uso del sitio se realiza bajo su propia responsabilidad.</p>
                    </Panel>

                    <Panel header="9. Derechos del titular de los datos" className="mb-4">
                        <p className="text-700">El usuario puede ejercer los derechos de:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li><strong>Acceso:</strong> conocer qué datos tenemos y cómo los usamos.</li>
                            <li><strong>Rectificación:</strong> corregir información inexacta o desactualizada.</li>
                            <li><strong>Cancelación:</strong> solicitar la eliminación de sus datos cuando no sean necesarios.</li>
                            <li><strong>Oposición:</strong> oponerse al uso de sus datos para ciertos fines (por ejemplo, marketing).</li>
                        </ul>
                        <p className="text-700 mt-3">Para ejercer estos derechos, el usuario debe enviar una solicitud a <a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a>, indicando su nombre completo, tipo y número de documento, y la descripción clara de su solicitud.</p>
                        <p className="text-700">La respuesta se brindará dentro del plazo legal establecido por la <strong>Autoridad Nacional de Protección de Datos Personales</strong>.</p>
                    </Panel>

                    <Panel header="10. Conservación de los datos" className="mb-4">
                        <p className="text-700">Los datos personales se conservarán durante el tiempo necesario para cumplir con las finalidades establecidas o según lo exija la legislación aplicable (por ejemplo, obligaciones tributarias o de facturación electrónica).</p>
                    </Panel>

                    <Panel header="11. Enlaces externos" className="mb-4">
                        <p className="text-700">Nuestro sitio puede contener enlaces a páginas de terceros (por ejemplo, redes sociales o plataformas de pago). <strong>Digital Wins</strong> no se hace responsable del contenido, seguridad ni prácticas de privacidad de dichos sitios externos.</p>
                    </Panel>

                    <Panel header="12. Modificaciones de esta política" className="mb-4">
                        <p className="text-700">Digital Wins se reserva el derecho de modificar esta Política de Privacidad en cualquier momento y sin previo aviso.</p>
                        <p className="text-700">Las versiones actualizadas se publicarán en esta misma sección y entrarán en vigor inmediatamente.</p>
                    </Panel>

                    <Panel header="13. Jurisdicción y ley aplicable" className="mb-4">
                        <p className="text-700">Esta Política se rige por las leyes de la <strong>República del Perú</strong>.</p>
                        <p className="text-700">Cualquier controversia será resuelta ante los tribunales competentes de la ciudad de Lima, renunciando expresamente a cualquier otro fuero.</p>
                    </Panel>

                    <Panel header="14. Contacto" className="mb-4">
                        <p className="text-700">Si tienes consultas sobre estos términos, puedes comunicarte con nosotros a través de:</p>
                        <ul className="list-none m-0 p-0 text-700">
                            <li>📧 <a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a></li>
                            <li>📱 WhatsApp: +51 978 326 796</li>
                        </ul>
                    </Panel>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PrivacidadPage;